import mitt from 'mitt'

// Create a new event emitter instance
const emitter = mitt()

/**
 * Event bus for Vue 3 using mitt
 *
 * Usage:
 *
 * 1. Import the event bus in your component:
 * ```
 * import { bus } from '@/global/bus';
 * ```
 *
 * 2. Emit an event:
 * ```
 * bus.emit('event-name', payload);
 * ```
 *
 * 3. Listen to an event:
 * ```
 * bus.on('event-name', (payload) => {
 *   // handle the event
 * });
 * ```
 *
 * 4. Remove event listener:
 * ```
 * // Remove specific listener
 * const handler = (payload) => console.log(payload);
 * bus.on('event-name', handler);
 * bus.off('event-name', handler);
 *
 * // Remove all listeners for an event
 * bus.off('event-name');
 *
 * // Remove all listeners
 * bus.all.clear();
 * ```
 */
export const bus = {
  /**
   * Emit an event
   * @param {string} event - Event name
   * @param {any} payload - Event data
   */
  emit: (event, payload) => emitter.emit(event, payload),

  /**
   * Subscribe to an event
   * @param {string} event - Event name
   * @param {Function} callback - Event handler
   */
  on: (event, callback) => emitter.on(event, callback),

  /**
   * Unsubscribe from an event
   * @param {string} event - Event name
   * @param {Function} [callback] - Event handler (if not provided, removes all handlers for this event)
   */
  off: (event, callback) => emitter.off(event, callback),

  /**
   * Access to all event handlers
   * Use this to clear all handlers: bus.all.clear()
   */
  all: emitter.all,
}

export default bus
